
















































  import Debug from 'debug'
import { Vue, Component, Prop, Emit, Watch } from 'vue-property-decorator';
import { SerializedHotspot, SerializedCoord } from '@xlearn/common';
import * as Api from "@/app/question/components/api";
import { HotspotAnswer, Spot, SpotState } from './hotspot-interfaces';
import { TranslateResult } from 'vue-i18n';
import i18n from '@/plugins/i18n';
import { waitToLoad, CancelToken, cancelToken, cancel } from '@/app/shared/utils';

interface Coordinate {
  x: number;
  y: number;
}

const debug = Debug('hotspot-question-component')

@Component({
  components: {}
})
export default class HotspotQuestionComponent extends Vue
implements Api.QuestionComponent<SerializedHotspot, HotspotAnswer, {}> {
  @Prop({
    required: true,
  })
  public question: Api.Question<SerializedHotspot>;

  @Prop({
    required: true,
  })
  public evaluated: boolean;

  @Prop({
    required: true,
  })
  public answer: Api.Answer<HotspotAnswer> | null;

  @Prop({
    required: true,
  })
  public showSolution: boolean;

  @Prop({
    required: true,
  })
  public state: null;

  private readonly mapName: string = 'hotspot_map';

  private markedSpots: Spot[] = [];

  private imageOriginalWidth: number = 0;
  private imageOriginalHeight: number = 0;
  private imageRenderedWidth: number = 0;
  private imageRenderedHeight: number = 0;

  private clickedCorrectIndices: number[] = [];
  private markedCorrect: boolean = false;
  private alreadyClicked: boolean = false;

  private cancelToken: CancelToken = cancelToken()

  private get componentId(): string {
    return `hotspot-question-${this.question.id}`;
  }

  private created(): void {

    this.imageDimensions().then((success: boolean) => {

      if (!success) {
        return
      }

      this.calculateImageRatio();

      if (this.answer) {
        this.clickedCorrectIndices = this.answer.answer.indices;
        this.markedSpots = this.answer.answer.markers;
        this.onEvaluated();
      }
    })
  }

  private destroyed() {
    cancel(this.cancelToken)
  }

  private serializeCoordinates(coords: SerializedCoord[]): string {
    let ratioX: number = this.getRatioX();
    let ratioY: number = this.getRatioY();

    if (!ratioX || !ratioY) {
      ratioX = 1
      ratioY = 1
    }

    const result = coords.reduce((prev: string, current: SerializedCoord) => {
      let items: string[] = [];
      if (prev !== '') {
        items = [...items, prev];
      }

      const correctedX: number = Math.round(current.x * ratioX);
      const correctedY: number = Math.round(current.y * ratioY);

      items = [...items, correctedX.toString(), correctedY.toString()];

      return items.join(',');
    }, '');

    return result
  }

  private handleCorrectClicked(index: number, remove?: boolean): void {
    if (this.evaluated) {
      return;
    }

    if (remove) {
      this.clickedCorrectIndices.splice(index, 1);
    } else if (!this.clickedCorrectIndices.includes(index)) {
      this.markedCorrect = true;

      this.clickedCorrectIndices = [...this.clickedCorrectIndices, index];
    } else {
      this.alreadyClicked = true;
    }
  }

  private deleteSpot(event: KeyboardEvent) {
    const target = <HTMLDivElement>event.target;
    const offsetX = Math.round(target.offsetWidth / 100 * this.cursorPosition.x);
    const offsetY = Math.round(target.offsetHeight / 100 * this.cursorPosition.y);

    this.markedSpots = this.markedSpots.filter((s, i) => {
      return !this.isPointInCircle(s, {
        x: offsetX,
        y: offsetY
      });

      this.handleCorrectClicked(i, true);
    });

    this.emitAnswerChanged();
  }

  private markSpot(event: MouseEvent): void {
    const target = <HTMLDivElement>event.target;

    if (this.evaluated) {
      return;
    }

    const ratioX = this.getRatioX();
    const ratioY = this.getRatioY();
    let { offsetX, offsetY } = event;

    if (!offsetX) offsetX = Math.round(target.offsetWidth / 100 * this.cursorPosition.x);
    if (!offsetY) offsetY = Math.round(target.offsetHeight / 100 * this.cursorPosition.y);

    this.question.content.areas.forEach((a, index) => {
      const coords = a.coordinates.map(c => ({
        x: c.x * ratioX,
        y: c.y * ratioY,
      }));

      if (this.isPointInTriangle(
        { x: offsetX, y: offsetY },
        coords[0] as Coordinate,
        coords[1] as Coordinate,
        coords[2] as Coordinate,
      )) {
        this.handleCorrectClicked(index);
      }
    });

    const $target = $(target);
    if ($target.hasClass('marker')) {
      return;
    }

    let state: SpotState = 'neutral';
    if (this.markedCorrect) {
      state = 'correct';
    } else if (!this.alreadyClicked) {
      state = 'incorrect';
    }

    this.markedSpots = [...this.markedSpots, {
      x: offsetX,
      y: offsetY,
      state
    }];

    this.markedCorrect = false;
    this.alreadyClicked = false;

    this.emitAnswerChanged();
  }

  @Emit('state-changed')
  public emitStateChanged(): Api.StateChangedPayload<{}> {
    return {
      state: {}
    }
  }

  @Emit('answer-changed')
  public emitAnswerChanged(): Api.AnswerChangedPayload<HotspotAnswer> {
    let score: number = 0;

    for (let i = 0; i < this.question.content.areas.length; i++) {
      let currentCorrect: boolean = true;
      if (!this.clickedCorrectIndices.includes(i)) {
        currentCorrect = false;
      }

      if (currentCorrect) {
        score += this.question.content.areas[i].score;
      }
    }

    return {
      score,
      answer: {
        indices: this.clickedCorrectIndices,
        markers: this.markedSpots,
      }
    };
  }

  @Watch('evaluated')
  private onEvaluatedChanged(newValue: boolean): void {
    if (!newValue) {
      this.setDefaults();
    } else {
      this.onEvaluated();
    }
  }

  private getSpotCssClass(state: SpotState): string {
    if (this.evaluated && (this.showSolution || this.allCorrect)) {
      return state;
    }

    return 'neutral';
  }

  private removeSpot(index: number): void {
    if (!this.evaluated) {
      this.markedSpots.splice(index, 1);
      this.handleCorrectClicked(index, true);
      this.emitAnswerChanged();
    }
  }

  private calculateMarkerPosition(x: number, y: number, isRendered: boolean) {
    let imageWidth: number = this.imageOriginalWidth;
    let imageHeight: number = this.imageOriginalHeight;
    if (isRendered) {
      imageWidth = this.imageRenderedWidth;
      imageHeight = this.imageRenderedHeight;
    }

    const left: number = x / imageWidth * 100;
    const top: number = y / imageHeight * 100;

    return `left: calc(${left}% - 12px); top: calc(${top}% - 12px)`;
  }

  private async imageDimensions() {

    debug('imageDimensions')

    const originalSize = await this.determineOriginalSize()
    this.imageOriginalWidth = originalSize.width;
    this.imageOriginalHeight = originalSize.height;

     debug('original size', this.imageOriginalWidth, this.imageOriginalHeight)

    let $image: JQuery<HTMLImageElement> | undefined
    const result = await waitToLoad(() => {

      const $container = $(`#${this.componentId}`);
      $image = $('.canvas img', $container);
      debug('length', $image.length)
      return $image.length > 0

    }, this.cancelToken)

    if (!result.success || !$image) {
      return false
    }

    this.imageRenderedWidth = $image.width() as number;
    this.imageRenderedHeight = $image.height() as number;

    debug('imageDimensions onLoad', this.imageOriginalWidth, this.imageOriginalHeight,
          this.imageRenderedWidth, this.imageRenderedHeight)
    debug($image)


    return true
  }

  private determineOriginalSize() {

    return new Promise<{ width: number, height: number }>((resolve) => {
      const img = new Image();
      img.onload = () => {
        resolve({
          width: img.width,
          height: img.height
        })
      }
      img.src = this.question.content.imageUri + '?' + Date.now();
      debug('determineOriginalSize', img.src);
    })
    }

    private setDefaults(): void {
      this.clickedCorrectIndices = [];
      this.markedSpots = [];
    }

    private get allCorrect(): boolean {
      return this.clickedCorrectIndices.length === this.question.content.areas.length;
    }

    private onEvaluated(): void {
      const length: number = this.question.content.areas.length;

      if (this.allCorrect) {
        return;
      }

      const ratioX: number = this.getRatioX();
      const ratioY: number = this.getRatioY();

      for (let i = 0; i < length; i++) {
        if (!this.clickedCorrectIndices.includes(i)) {
          const area = this.question.content.areas[i];
          const centroid = this.getPolygonCentroid(area.coordinates);
          this.markedSpots = [...this.markedSpots, {
            x: centroid.x * ratioX,
            y: centroid.y * ratioY,
            state: 'not-marked'
          }];
        }
      }
    }

    private getRatioX(): number {
      return this.imageRenderedWidth / this.imageOriginalWidth;
    }

    private getRatioY(): number {
      return this.imageRenderedHeight / this.imageOriginalHeight;
    }

    // https://stackoverflow.com/questions/9692448/how-can-you-find-the-centroid-of-a-concave-irregular-polygon-in-javascript
    private getPolygonCentroid(points: SerializedCoord[]) {
      var first = points[0], last = points[points.length-1];
      if (first.x != last.x || first.y != last.y) points.push(first);
      var twicearea=0,
        x=0, y=0,
        nPts = points.length,
        p1, p2, f;
      for ( var i=0, j=nPts-1 ; i<nPts ; j=i++ ) {
        p1 = points[i]; p2 = points[j];
        f = p1.x*p2.y - p2.x*p1.y;
        twicearea += f;
        x += ( p1.x + p2.x ) * f;
        y += ( p1.y + p2.y ) * f;
      }
      f = twicearea * 3;
      return { x:x/f, y:y/f };
    }

    private sign(p1: Coordinate, p2: Coordinate, p3: Coordinate) {
      return (p1.x - p3.x) * (p2.y - p3.y) - (p2.x - p3.x) * (p1.y - p3.y);
    }

    private isPointInTriangle(pt: Coordinate, v1: Coordinate, v2: Coordinate, v3: Coordinate)
    {
        let d1: number, d2: number, d3: number;
        let has_neg: boolean, has_pos: boolean;

        d1 = this.sign(pt, v1, v2);
        d2 = this.sign(pt, v2, v3);
        d3 = this.sign(pt, v3, v1);

        has_neg = (d1 < 0) || (d2 < 0) || (d3 < 0);
        has_pos = (d1 > 0) || (d2 > 0) || (d3 > 0);

        return !(has_neg && has_pos);
    }

    private isPointInCircle(p: Coordinate, c: Coordinate) {
      return Math.sqrt(Math.pow(p.x - c.x, 2) + Math.pow(p.y - c.y, 2)) < 12; // 12 is the marker dot radius
    }

    private cursorPosition = { x: 50, y: 50 }

    private moveCursor(e: KeyboardEvent, dir: string) {
      (this.$refs.canvas as HTMLAnchorElement).classList.add('started');

      switch (dir) {
        case 'left':
            this.cursorPosition.x = Math.max(this.cursorPosition.x - 1, 0);
          break;
        case 'right':
            this.cursorPosition.x = Math.min(this.cursorPosition.x + 1, 100);
          break;
        case 'up':
            this.cursorPosition.y = Math.max(this.cursorPosition.y - 1, 0);
          break;
        case 'down':
            this.cursorPosition.y = Math.min(this.cursorPosition.y + 1, 100);
          break;
        default:
          break;
      }
    }

    private calculateImageRatio() {
      const image = this.$refs.image as HTMLImageElement;
      this.imageRenderedWidth = image.offsetWidth;
      this.imageRenderedHeight = image.offsetHeight;
    }

    private t(key: string, params: any = {}): TranslateResult {
      return i18n.t(key, params);
    }
  }
